import { DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useApi from "../../../shared/hooks/useApi";
import { PatientServicePlanStatus } from "../../../shared/schema/gql/graphql";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import { getFullName } from "../../../shared/utils/get-full-name";
import { GET_SERVICE_PLANS_QUERY } from "../service-plan.graphql";
import { ServicePlanRow } from "../types/service-plan-dashboard.types";
import ServicePlanEditorModal from "./ServicePlanEditorModal";

interface Props {
  plan: ServicePlanRow;
}

/**
 * The same row actions the patient profile offers, driven off a dashboard row:
 * edit (view once finalized), open the PDF, and delete a draft.
 */
export default function ServicePlanTableActions({ plan }: Props) {
  const { mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const deleteDisclosure = useDisclosure();

  const isDraft = plan.status === PatientServicePlanStatus.Draft;

  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: [GET_SERVICE_PLANS_QUERY] });

  const pdfUrlMutation = useMutation({
    ...mutations.servicePlan.getPdfUrl(),
    onSuccess: ({ signedUrl }) => {
      if (signedUrl === null) {
        toast({
          title: "No PDF for this plan",
          status: "warning",
          position: "top-right",
        });
        return;
      }
      window.open(signedUrl, "_blank");
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not open the PDF",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const deleteMutation = useMutation({
    ...mutations.servicePlan.deleteServicePlan(),
    onSuccess: () => {
      invalidateList();
      deleteDisclosure.onClose();
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not delete the service plan",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  return (
    <>
      <HStack spacing={1}>
        <Button size="sm" variant="outline" onClick={() => setIsEditorOpen(true)}>
          {isDraft ? "Edit" : "View"}
        </Button>
        {plan.hasPdf && (
          <Button
            isLoading={pdfUrlMutation.isPending}
            size="sm"
            variant="outline"
            onClick={() => pdfUrlMutation.mutate(plan.id)}
          >
            PDF
          </Button>
        )}
        {isDraft && (
          <IconButton
            aria-label="Delete service plan"
            colorScheme="red"
            icon={<DeleteIcon />}
            size="md"
            variant="ghost"
            onClick={deleteDisclosure.onOpen}
          />
        )}
      </HStack>

      {isEditorOpen && (
        <ServicePlanEditorModal
          isOpen={true}
          patientId={plan.patient.id}
          servicePlanId={plan.id}
          onClose={() => {
            setIsEditorOpen(false);
            invalidateList();
          }}
        />
      )}

      <Modal isOpen={deleteDisclosure.isOpen} onClose={deleteDisclosure.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Service Plan</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete this draft service plan for{" "}
              {getFullName(plan.patient)}? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={deleteDisclosure.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(plan.id)}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
